import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { IconButton, Tooltip } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { useSelector } from 'react-redux';
import { getHoldingSettlementDetails } from '../../helper/Api';
import { AddCommasToAmount, downloadSafeFile } from '../../helper/Utils';
import PpaDeductionDetailsCell from '../Ppa/PpaDeductionDetailsCell';
import PpaSchemeInfoCell from '../Ppa/PpaSchemeInfoCell';
import {
    PpaSchemeInfoExpandProvider,
    PpaSchemeInfoColumnHeader,
} from '../Ppa/PpaSchemeInfoExpandContext';
import DepartmentIdentityCell from '../Tools/DepartmentIdentityCell';

const getPpaRecordType = (row) => {
    const prefix = String(row?.ppa_no || '').trim().charAt(0).toUpperCase();
    if (prefix === 'R' || row?.beneficiary_reimbursement_id) return 'reimbursement';
    if (prefix === 'S' || row?.salary_id) return 'salary';
    return 'invoice';
};

const getVoucherOrderDisplay = (row) => {
    const type = getPpaRecordType(row);
    if (type === 'reimbursement') return row?.beneficiary_reimbursement_id || row?.voucher_order_no || '-';
    if (type === 'salary') return row?.salary_id || '-';
    return row?.voucher_order_no || row?.invoice_no || row?.invoice_id || '-';
};

const getBeneficiaryDisplay = (row) => {
    const type = getPpaRecordType(row);
    if (type === 'reimbursement') {
        return {
            name: row?.reimbursement_beneficiary_name || row?.beneficiary_name || '-',
            account: row?.reimbursement_beneficiary_account_no || row?.beneficiary_account_no || '-',
            ifsc: row?.reimbursement_beneficiary_ifsc_code || row?.beneficiary_ifsc || '-',
        };
    }
    return {
        name: row?.beneficiary_name || row?.invoice_beneficiary_name || '-',
        account: row?.beneficiary_account_no || '-',
        ifsc: row?.beneficiary_ifsc || row?.beneficiary_ifsc_code || '-',
    };
};

const formatTableAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return AddCommasToAmount(value);
};

/** Holding ACH status (not beneficiary bank status). */
const getHoldingPaymentStatus = (flag, reasonCode) => {
    const f = flag === null || flag === undefined ? null : String(flag);
    const reason = String(reasonCode || '').trim();

    if (f === '1') {
        return { label: 'Success', color: '#d4edda', textColor: '#155724' };
    }
    if (f === '3') {
        return { label: 'On Hold', color: '#fff3cd', textColor: '#856404' };
    }
    if (f === '0') {
        return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
    }
    if (f === '2') {
        return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
    }
    if (reason && reason !== '00') {
        return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
    }
    if (f === null || f === '' || f === 'null') {
        return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
    }
    return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
};

const getPaymentModeChip = (paymentMode) => {
    const mode = String(paymentMode || '').toLowerCase();
    if (mode === 'online') {
        return { label: 'Online', bg: '#e8f0ff', color: '#1f4db8' };
    }
    if (mode === 'offline') {
        return { label: 'Offline', bg: '#e6f4ea', color: '#1e7e34' };
    }
    return { label: mode || '—', bg: '#f3f4f6', color: '#374151' };
};

const extractDetailsList = (payload) => {
    if (!payload) return [];
    const candidates = [payload.data, payload.result, payload.list, payload.result?.data];
    for (const c of candidates) {
        if (Array.isArray(c)) return c.filter(Boolean);
    }
    return [];
};

/**
 * Nested expandable table for one holding settlement.
 * Layout mirrors Consolidate Beneficiary PPA; status = holding payment status.
 */
const HoldingSettlementDetailsExpand = ({ data: parentRow, onViewTrail, onViewInvoice }) => {
    const settlementId = parentRow?.id;
    const { authData } = useSelector((state) => state.authData);
    const departmentId = authData?.user?.department_id;
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!settlementId) {
                setRows([]);
                return;
            }
            try {
                setLoading(true);
                const res = await getHoldingSettlementDetails({
                    holding_settlement_id: settlementId,
                    department_id: departmentId,
                });
                if (cancelled) return;
                if (res?.data?.status) {
                    setRows(
                        extractDetailsList(res.data).map((row, index) => ({
                            ...row,
                            rowKey:
                                row?.id != null
                                    ? String(row.id)
                                    : row?.holding_file_detail_id != null
                                      ? `hfd-${row.holding_file_detail_id}`
                                      : `detail-${settlementId}-${index}`,
                        }))
                    );
                } else {
                    setRows([]);
                }
            } catch (_e) {
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [settlementId, departmentId]);

    const columns = [
        {
            name: 'Actions',
            width: '150px',
            cell: (row) => (
                <div className="holding-settlement-details-actions">
                    <Tooltip title="Download PPA" arrow>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!row?.ppa_url}
                                onClick={() => row?.ppa_url && downloadSafeFile(row.ppa_url)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #1976d2',
                                    color: '#1976d2',
                                    borderRadius: '6px',
                                }}
                            >
                                <AiOutlineVerticalAlignBottom size={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Holding / Payment Trail" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => onViewTrail?.(row)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #f59e0b',
                                    color: '#f59e0b',
                                    borderRadius: '6px',
                                }}
                            >
                                <HistoryOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {row?.invoice_id ? (
                        <Tooltip title="View Invoice" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onViewInvoice?.(row)}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        border: '1px solid #17a2b8',
                                        color: '#17a2b8',
                                        borderRadius: '6px',
                                    }}
                                >
                                    <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </div>
            ),
        },
        {
            name: 'PPA Info',
            width: '150px',
            wrap: true,
            cell: (row) => {
                const modeChip = getPaymentModeChip(row?.payment_mode);
                const holdingFlag =
                    row?.holding_flag_success_return ?? row?.flag_success_return;
                const holdingReason =
                    row?.holding_reason_code ?? row?.reason_code;
                const statusMeta = getHoldingPaymentStatus(holdingFlag, holdingReason);

                return (
                    <div style={{ lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 600 }}>PPA: {row?.ppa_no || '-'}</div>
                        <div style={{ marginTop: 4 }}>
                            <span
                                style={{
                                    padding: '3px 8px',
                                    borderRadius: 999,
                                    backgroundColor: modeChip.bg,
                                    color: modeChip.color,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: 'inline-block',
                                }}
                            >
                                {modeChip.label}
                            </span>
                        </div>
                        <div style={{ marginTop: 6 }}>
                            <span
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    backgroundColor: statusMeta.color,
                                    color: statusMeta.textColor,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: 'inline-block',
                                }}
                            >
                                {statusMeta.label}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            name: 'Department (Tier / Code)',
            width: '130px',
            wrap: true,
            cell: (row) => (
                <DepartmentIdentityCell
                    row={row}
                    showNomenclatureLabel={false}
                    fallbackDistrictRow={authData?.user?.department_details}
                />
            ),
        },
        {
            // Aligned under parent "Amount" (Unique ID 150 + Sett 150 + Status 130)
            name: 'Deduction Details',
            width: '130px',
            wrap: true,
            cell: (row) => <PpaDeductionDetailsCell row={row} />,
        },
        {
            name: <PpaSchemeInfoColumnHeader />,
            minWidth: '200px',
            wrap: true,
            cell: (row) => <PpaSchemeInfoCell row={row} />,
        },
        {
            name: 'voucher / order No',
            minWidth: '140px',
            cell: (row) => (
                <span style={{ fontWeight: 600 }}>{getVoucherOrderDisplay(row)}</span>
            ),
        },
        {
            name: 'Beneficiary Info',
            minWidth: '180px',
            cell: (row) => {
                const { name, account, ifsc } = getBeneficiaryDisplay(row);
                return (
                    <div style={{ lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 600 }}>{name}</div>
                        <div style={{ color: '#6b7280', marginTop: 4 }}>A/C: {account}</div>
                        <div style={{ color: '#6b7280', marginTop: 2 }}>IFSC: {ifsc}</div>
                    </div>
                );
            },
        },
        {
            name: 'Amount Info',
            minWidth: '150px',
            cell: (row) => {
                const type = getPpaRecordType(row);
                const sanctioned =
                    type === 'reimbursement'
                        ? row?.reimbursement_gross_amount ?? row?.invoice_sanctioned_amount
                        : row?.invoice_sanctioned_amount ?? row?.sanctioned_amount;
                const deduction =
                    type === 'reimbursement'
                        ? row?.reimbursement_deduction_amount ?? row?.invoice_total_deduction
                        : row?.invoice_total_deduction ?? row?.deduction_amount ?? row?.holding_amount;
                const payment =
                    type === 'reimbursement'
                        ? row?.reimbursement_net_amount ?? row?.invoice_total_payable
                        : row?.invoice_total_payable ?? row?.payment_amount;
                return (
                    <div style={{ lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 600 }}>
                            Sanctioned: {formatTableAmount(sanctioned)}
                        </div>
                        <div style={{ color: '#6b7280', marginTop: 4 }}>
                            Deduction: {formatTableAmount(deduction)}
                        </div>
                        <div style={{ color: '#6b7280', marginTop: 2 }}>
                            Payment: {formatTableAmount(payment)}
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="holding-settlement-details-expand">
            <PpaSchemeInfoExpandProvider>
            <DataTable
                dense
                columns={columns}
                data={rows}
                keyField="rowKey"
                progressPending={loading}
                noDataComponent={
                    <div className="p-3 text-muted">
                        No invoice records found for this settlement.
                    </div>
                }
                customStyles={{
                    headCells: {
                        style: {
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: '#fff',
                            borderBottom: '1px solid #e8eaef',
                        },
                    },
                    cells: {
                        style: {
                            fontSize: '12px',
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            backgroundColor: '#fff',
                        },
                    },
                    rows: {
                        style: {
                            backgroundColor: '#fff',
                        },
                    },
                }}
            />
            </PpaSchemeInfoExpandProvider>
        </div>
    );
};

export default HoldingSettlementDetailsExpand;
